<template>
    <app-layout v-if="isPageReady && isReferralMade" printClass="print-referral-summary" class="referral-pool-confirmation">
        <common-content-spacer height="small">
            <common-page-header :heading="$t('pages.referrals.new.steps.confirmation.selection_sent')"></common-page-header>

            <common-page-plain :hasLargePadding="true">
                <referrals-confirmation :heading="$t('pages.referrals.new.steps.confirmation.heading_pool')" type="success">
                    <template #content>
                        <common-summary>
                            <common-summary-item :label="$t('pages.referrals.new.steps.confirmation.summary.patient_name')">
                                <common-user-name :user="referral.patient"></common-user-name>
                            </common-summary-item>

                            <common-summary-item>
                                <p>{{ selectedTimeslot.date }}</p>
                                <p>{{ selectedTimeslot.startTime }} - {{ selectedTimeslot.endTime }}</p>
                            </common-summary-item>

                            <common-summary-item :label="$t('pages.referrals.new.steps.confirmation.summary.diagnosis')">
                                {{ referral.diagnosis }}
                            </common-summary-item>

                            <common-summary-item
                                :label="$t('pages.referrals.new.steps.confirmation.summary.to')"
                                :isInline="true"
                            >
                                <common-user-name :user="selectedUser"></common-user-name>

                                <br />

                                <p>
                                    {{ $t('pages.referrals.new.steps.confirmation.summary.tel', { phone: selectedUser?.phone }) }}
                                </p>
                            </common-summary-item>
                        </common-summary>
                    </template>

                    <template #footer>
                        <common-buttons>
                            <common-button
                                :label="$t('pages.referrals.new.steps.confirmation.summary.print')"
                                :isSubmittable="false"
                                type="primary"
                                @click="printSummary()"
                            ></common-button>
                        </common-buttons>
                    </template>
                </referrals-confirmation>
            </common-page-plain>
        </common-content-spacer>
    </app-layout>
    <app-layout v-else-if="isPageReady">
        <common-content-spacer height="medium">
            <common-content-spacer height="small">
                <common-breadcrumbs :breadcrumbs="pageBreadcrumbs"></common-breadcrumbs>

                <common-page-header :heading="pageTitle"></common-page-header>
            </common-content-spacer>

            <common-page-plain v-if="showOnlyProposedTimeSlots" class="page-split__referrals-pools">
                <common-content-spacer height="medium">
                    <div v-if="getUsersWithProposedTimeSlots?.length">
                        <common-content-spacer height="medium">
                            <common-page-header
                                :heading="
                                    isSsnEmpty
                                        ? $t('pages.referrals.pool.update_patient_choose_provider')
                                        : $t('pages.referrals.pool.choose_provider')
                                "
                                size="page-plain-header"
                                :isCenter="true"
                                contentSpacing="medium"
                            ></common-page-header>
                            <div v-if="isSsnEmpty" class="patient-info-form">
                                <upload-note-modal
                                    ref="uploadNoteModal"
                                    :referral="data"
                                    :isOpen="isUploadNoteModalOpen"
                                    @files-uploaded="setUploadedFiles"
                                    @close="closeUploadNoteModal"
                                ></upload-note-modal>
                                <form-wrapper>
                                    <form-row v-if="proposedSlotOptions.length">
                                        <form-cell width="full">
                                            <radio-field
                                                :label="$t('pages.referrals.pool.select_referral_slots')"
                                                :options="proposedSlotOptions"
                                                :formData="data"
                                                valuePath="selectedTimeslot"
                                                :isRequired="true"
                                                @change="change($event)"
                                            ></radio-field>
                                        </form-cell>
                                    </form-row>
                                    <form-row>
                                        <form-cell width="half">
                                            <text-field
                                                :label="$t('forms.referrals.make.fields.patient_first_name.label')"
                                                :placeholder="$t('forms.referrals.make.fields.patient_first_name.placeholder')"
                                                :isRequired="true"
                                                :formData="data"
                                                :isSuggested="false"
                                                valuePath="patient.firstName"
                                                @change="change($event)"
                                            ></text-field>
                                        </form-cell>

                                        <form-cell width="half">
                                            <text-field
                                                :label="$t('forms.referrals.make.fields.patient_last_name.label')"
                                                :placeholder="$t('forms.referrals.make.fields.patient_last_name.placeholder')"
                                                :isRequired="true"
                                                :isSuggested="false"
                                                :formData="data"
                                                valuePath="patient.lastName"
                                                @change="change($event)"
                                            ></text-field>
                                        </form-cell>
                                    </form-row>
                                    <form-row>
                                        <form-cell width="full">
                                            <birth-date-field
                                                :label="$t('forms.referrals.make.fields.patient_birth_date.label')"
                                                :isRequired="true"
                                                :isSuggested="false"
                                                :formData="data"
                                                valuePath="patient.dateOfBirth"
                                                @change="change($event)"
                                            ></birth-date-field>
                                        </form-cell>
                                    </form-row>
                                    <form-row>
                                        <form-cell width="half">
                                            <text-field
                                                :label="$t('forms.referrals.make.fields.patient_ssn.label')"
                                                :placeholder="$t('forms.referrals.make.fields.patient_ssn.placeholder')"
                                                :isRequired="true"
                                                :isSuggested="false"
                                                :formData="data"
                                                valuePath="patient.ssn"
                                                @change="change($event)"
                                            ></text-field>
                                        </form-cell>
                                        <form-cell width="half">
                                            <phone-field
                                                :label="$t('forms.referrals.make.fields.patient_mobile_phone.label')"
                                                :placeholder="$t('common.phone_number.placeholder')"
                                                :hint="$t('forms.referrals.make.fields.patient_mobile_phone.info')"
                                                :isRequired="true"
                                                :isSuggested="false"
                                                :formData="data"
                                                valuePath="patient.mobilePhone"
                                                @change="change($event)"
                                                @country-code-change="onCountryCodeChange($event)"
                                            ></phone-field>
                                        </form-cell>
                                    </form-row>
                                    <form-row>
                                        <form-cell class="referral-details__text-editor">
                                            <text-editor-field
                                                :placeholder="$t('forms.referrals.make.fields.note.placeholder')"
                                                :label="$t('forms.referrals.make.fields.note.label')"
                                                :formData="data"
                                                :uploadedFiles="filesAlreadyUploaded"
                                                :overlayText="$t('forms.referrals.make.fields.note.overlay')"
                                                valuePath="note"
                                                @change="change($event)"
                                                @attach-file="openUploadNoteModal"
                                                @remove-file="removeFile($event)"
                                            ></text-editor-field>
                                        </form-cell>
                                    </form-row>
                                    <div v-if="showRequiredFieldsError" class="required-fields-error">
                                        {{ $t('pages.referrals.pool.required_fields_error') }}
                                    </div>
                                    <common-buttons class="continue_btn">
                                        <common-button
                                            :label="$t('common.microcopy.continue')"
                                            :isSubmittable="false"
                                            type="primary"
                                            @click="continueClick()"
                                        ></common-button>
                                    </common-buttons>
                                </form-wrapper>
                            </div>
                            <div v-if="!isSsnEmpty" class="proposed-time-slots">
                                <div
                                    v-for="(item, index) in getUsersWithProposedTimeSlots"
                                    :key="index"
                                    class="proposed-time-slots--item"
                                >
                                    <div
                                        :class="{ 'proposed-time-slots--user--open': item.isOpen }"
                                        class="proposed-time-slots--user"
                                        @click="expandItem(item)"
                                    >
                                        <user-details
                                            :user="item"
                                            :bottomText="
                                                item?.distance ? `${item?.distance} ${$t('pages.referrals.pool.miles_away')}` : ''
                                            "
                                        ></user-details>
                                        <common-icon :name="item.isOpen ? 'minus' : 'referral-add'" size="large"></common-icon>
                                    </div>
                                    <transition name="slide-up">
                                        <common-content-spacer v-if="item.isOpen" height="tiny">
                                            <div
                                                v-for="(slots, date) in groupSlotsByDateForUser(item.proposedTimeSlots)"
                                                :key="date"
                                                class="proposed-time-slots--item__times"
                                            >
                                                <div>
                                                    <b>{{ formatDate(date) }}</b>
                                                </div>
                                                <div v-for="(slot, i) in slots" :key="i" class="slot-selection">
                                                    <common-button
                                                        :label="`${slot.startTime} - ${slot.endTime}`"
                                                        :isSubmittable="false"
                                                        type="secondary"
                                                        @click="openAreYouSureModal(slot, item)"
                                                    ></common-button>
                                                </div>
                                            </div>
                                        </common-content-spacer>
                                    </transition>
                                </div>
                            </div>
                        </common-content-spacer>
                    </div>
                    <common-button
                        v-else
                        :label="`${getUsersWithProposedTimeSlotsLabel}`"
                        :isSubmittable="false"
                        type="link"
                        @click="openProvidersModal(referral.id)"
                    ></common-button>
                </common-content-spacer>
            </common-page-plain>

            <common-page-plain v-else-if="referral?.status === ReferralStatusEnum.INACTIVE">
                <common-content-spacer height="small">
                    <referrals-status :referral="referral" :isInline="true"></referrals-status>
                    <referral-details :referral="referral"></referral-details>
                    <common-buttons v-if="referral?.status === ReferralStatusEnum.CREATED" align="center">
                        <common-button
                            v-if="!$auth.getUser().practiceId"
                            :label="$t('common.microcopy.accept')"
                            :isSubmittable="false"
                            type="primary"
                            @click="togglePracticeCreationModal()"
                        ></common-button>
                    </common-buttons>
                </common-content-spacer>
            </common-page-plain>

            <common-page-split v-else-if="referral?.id" class="page-split__referrals-pools">
                <template #aside>
                    <div v-if="type === ReferralPathTypeEnum.RECEIVED">
                        <common-content-spacer height="medium">
                            <practice-details v-if="showPracticeDetails" :practice="getPracticeDetails"></practice-details>
                            <user-details
                                v-else
                                :user="physicianData"
                                :bottomText="physicianSpeciality"
                                :bottomText2="getCreatedDate"
                            ></user-details>
                            <common-content-spacer
                                v-if="
                                    !![
                                        ReferralStatusEnum.TIME_SLOTS_PROPOSED,
                                        ReferralStatusEnum.SCHEDULED,
                                        ReferralStatusEnum.INACTIVE,
                                    ].includes(referral.status)
                                "
                                height="medium"
                            >
                                <common-page-header
                                    :heading="
                                        referral.proposedTimeSlots?.length
                                            ? $t('pages.referrals.pool.your_proposed_slots')
                                            : $t('pages.referrals.pool.you_did_not_propose')
                                    "
                                    size="page-plain-header"
                                ></common-page-header>
                                <referral-time-slots-preview
                                    v-if="referral.proposedTimeSlots?.length"
                                    :groupSlotsByDate="groupSlotsByDate"
                                ></referral-time-slots-preview>
                            </common-content-spacer>
                        </common-content-spacer>
                    </div>

                    <div v-else>
                        <common-content-spacer height="medium">
                            <div v-if="hideAsideWhenTimeSlotsProposed">
                                <common-content-spacer height="medium">
                                    <common-page-header
                                        :heading="$t('pages.referrals.pool.choose_provider')"
                                        size="page-plain-header"
                                        :isCenter="true"
                                        contentSpacing="medium"
                                    ></common-page-header>

                                    <div class="proposed-time-slots">
                                        <div
                                            v-for="(item, index) in getUsersWithProposedTimeSlots"
                                            :key="index"
                                            class="proposed-time-slots--item"
                                        >
                                            <div
                                                :class="{ 'proposed-time-slots--user--open': item.isOpen }"
                                                class="proposed-time-slots--user"
                                                @click="expandItem(item)"
                                            >
                                                <user-details
                                                    :user="item"
                                                    :bottomText="
                                                        item?.distance
                                                            ? `${item?.distance} ${$t('pages.referrals.pool.miles_away')}`
                                                            : ''
                                                    "
                                                ></user-details>
                                                <common-icon
                                                    :name="item.isOpen ? 'minus' : 'referral-add'"
                                                    size="large"
                                                ></common-icon>
                                            </div>
                                            <transition name="slide-up">
                                                <common-content-spacer v-if="item.isOpen" height="tiny">
                                                    <div
                                                        v-for="(slots, date) in groupSlotsByDateForUser(item.proposedTimeSlots)"
                                                        :key="date"
                                                        class="proposed-time-slots--item__times"
                                                    >
                                                        <div>
                                                            <b>{{ formatDate(date) }}</b>
                                                        </div>
                                                        <div v-for="(slot, i) in slots" :key="i" class="slot-selection">
                                                            <common-button
                                                                :label="`${slot.startTime} - ${slot.endTime}`"
                                                                :isSubmittable="false"
                                                                type="secondary"
                                                                @click="openAreYouSureModal(slot, item)"
                                                            ></common-button>
                                                        </div>
                                                    </div>
                                                </common-content-spacer>
                                            </transition>
                                        </div>
                                    </div>
                                </common-content-spacer>
                            </div>
                            <common-button
                                v-else-if="showToHowManyProvidersIsSent"
                                :label="`${getUsersWithProposedTimeSlotsLabel}`"
                                :isSubmittable="false"
                                type="link"
                                @click="openProvidersModal(referral.id)"
                            ></common-button>
                            <div v-else>
                                <practice-details v-if="isSentToAncillaries" :practice="toProvider"></practice-details>
                                <user-details v-else :user="toProvider" :bottomText2="getCreatedDate"></user-details>
                            </div>
                        </common-content-spacer>
                    </div>
                </template>

                <template #main>
                    <common-content-spacer height="small">
                        <referrals-status :referral="referral" :isInline="true"></referrals-status>
                        <referral-details :referral="referral" :isReferralPool="true"></referral-details>
                        <common-buttons v-if="referral.status === ReferralStatusEnum.CREATED" align="center">
                            <subscribe-to-premium-tooltip-wrapper :show="isToUserSubscriptionNetwork">
                                <common-button
                                    v-if="isReferred"
                                    :label="$t('pages.referrals.pool.accept_and_propose_time')"
                                    :isProcessing="isProcessing"
                                    :disabled="isToUserSubscriptionNetwork"
                                    :isSubmittable="false"
                                    type="primary"
                                    @click="processReferral()"
                                ></common-button>
                            </subscribe-to-premium-tooltip-wrapper>
                        </common-buttons>
                    </common-content-spacer>
                </template>
            </common-page-split>
        </common-content-spacer>
        <referrals-pool-providers-modal
            v-if="showReferralsPoolProvidersModal"
            :referralPoolId="Number($route.params.id)"
            @close="closeProvidersModal"
        ></referrals-pool-providers-modal>
        <available-locations-modal
            v-if="showAvailableLocationsModal"
            :referral="referral"
            :locations="locations"
            @close="closeAvailableLocationsModal"
        ></available-locations-modal>
        <referral-confirm-slot-modal
            v-if="isAreYouSureModalOpen"
            :selectedUser="selectedUser"
            :selectedTimeslot="selectedTimeslot"
            @close="closeAreYouSureModal"
            @continue="confirmTimeSlot"
        ></referral-confirm-slot-modal>
    </app-layout>
</template>

<script>
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import PageWithElementsMixin from 'mixins/PageWithElementsMixin'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import AppLayout from 'components/layouts/AppLayout'
    import ReferralsPoolAPI from 'api/ReferralsPoolAPI'
    import ReferralsAPI from 'api/ReferralsAPI'
    import ReferralStatusMixin from 'mixins/ReferralStatusMixin'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'
    import ReferralPathTypeEnum from '@/enums/ReferralPathTypeEnum'
    import ReferralsPoolProvidersModal from 'components/page-elements/referrals/ReferralsPoolProvidersModal'
    import ReferralDetails from 'components/page-elements/referrals/ReferralDetails'
    import ReferralsStatus from 'components/page-elements/ReferralsStatus'
    import UserDetails from 'components/page-elements/referrals/details/UserDetailsCard'
    import PracticeDetails from 'components/page-elements/referrals/details/PracticeDetailsCard'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import dayjs from 'dayjs'
    import AvailableLocationsModal from 'components/page-elements/referrals/AvailableLocationsModal.vue'
    import ReferralStatusEnum from '@/enums/ReferralStatusEnum'
    import ReferralTimeSlotsPreview from 'components/page-elements/referrals/ReferralTimeSlotsPreview.vue'
    import ReferralConfirmSlotModal from 'components/page-elements/referrals/ReferralConfirmSlotModal.vue'
    import PracticesAPI from 'api/PracticesAPI'
    import FormMixin from 'mixins/FormMixin'
    import UploadNoteModal from 'components/page-elements/referrals/UploadNoteModal'
    import PracticePlanEnum from '@/enums/PracticePlanEnum'
    import SubscribeToPremiumTooltipWrapper from '../../../components/page-elements/SubscribeToPremiumTooltipWrapper.vue'

    export default {
        mixins: [
            PageWithTitleMixin,
            PageWithElementsMixin,
            PageWithDataMixin,
            ReferralStatusMixin,
            ScreenSizeDetectionMixin,
            FormMixin,
        ],

        components: {
            AppLayout,
            ReferralsPoolProvidersModal,
            ReferralDetails,
            ReferralsStatus,
            UserDetails,
            PracticeDetails,
            AvailableLocationsModal,
            ReferralTimeSlotsPreview,
            ReferralConfirmSlotModal,
            UploadNoteModal,
            SubscribeToPremiumTooltipWrapper,
        },

        data() {
            return {
                referral: {},
                showReferralsPoolProvidersModal: false,
                showAvailableLocationsModal: false,
                ReferralPathTypeEnum: ReferralPathTypeEnum,
                ReferralStatusEnum: ReferralStatusEnum,
                isAreYouSureModalOpen: false,
                selectedTimeslot: null,
                selectedUser: null,
                isReferralMade: false,
                isProcessing: false,
                showRequiredFieldsError: false,
                data: {},
                uploadedFiles: [],
                isUploadNoteModalOpen: false,
            }
        },

        provide() {
            return {
                isReferralPool: !!this.$route.params.type,
            }
        },

        computed: {
            isToUserSubscriptionNetwork() {
                return this.$auth.getPractice().subscription === PracticePlanEnum.NETWORK
            },
            toProvider() {
                return this.referral?.toProviders[0] || null
            },
            toProviderTimeSlots() {
                return this.referral?.toProviders?.[0]?.proposedTimeSlots || []
            },
            proposedSlotOptions() {
                const slots = this.toProviderTimeSlots
                if (!slots) return []

                return slots.map((slot) => {
                    const value = `${slot.date}|${slot.startTime}`

                    return {
                        label: `${slot.startTime} - ${slot.endTime} • ${slot.date}`,
                        value,
                    }
                })
            },
            proposedSlotMap() {
                const slots = this.toProviderTimeSlots
                if (!slots) return []

                return slots.reduce((acc, slot) => {
                    acc[`${slot.date}|${slot.startTime}`] = slot
                    return acc
                }, {})
            },
            filesAlreadyUploaded() {
                return this.uploadedFiles.filter((file) => !!file.file?.id)
            },
            isSsnEmpty() {
                return this.referral?.patient?.ssn === '0000'
            },
            groupSlotsByDate() {
                return this.groupSlotsByDateForUser(this.referral.proposedTimeSlots)
            },
            isReferred() {
                return this.$route.params.type === ReferralPathTypeEnum.RECEIVED
            },

            type() {
                return this.$route.params.type
            },

            getUsersWithProposedTimeSlots() {
                return this.referral.toProviders
                    .filter((item) => item.proposedTimeSlots?.length)
                    .map((user) => {
                        if (typeof user.isOpen === 'undefined') {
                            this.$set(user, 'isOpen', false)
                        }
                        return user
                    })
            },

            getUsersWithProposedTimeSlotsLabel() {
                return `${this.getUsersWithProposedTimeSlots.length} / ${this.referral.numberOfProviders} providers has proposed time slots`
            },

            pageTitle() {
                return this.$t(`pages.referrals.show.pool.referrap_pool.${this.$route.params.type}`, {
                    name: this.getReferralTypeName,
                    numberOfProviders: this.referral.numberOfProviders,
                })
            },

            showPracticeDetails() {
                if (this.type === ReferralPathTypeEnum.RECEIVED) {
                    return this.referral.fromPractice?.practiceType === PracticeTypeEnum.ANCILLARY
                } else {
                    return this.referral.practice?.practiceType === PracticeTypeEnum.ANCILLARY
                }
            },

            isSentToAncillaries() {
                return this.toProvider?.practiceType === PracticeTypeEnum.ANCILLARY
            },

            getPracticeDetails() {
                if (this.type === ReferralPathTypeEnum.RECEIVED) {
                    return this.referral.fromPractice
                } else {
                    return this.referral.practice
                }
            },

            physicianData() {
                return this.referral.fromUser
            },

            physicianSpeciality() {
                return this.$auth.getUser().id === this.referral.fromUser?.id
                    ? this.referral.toUser?.specialities[0]?.speciality?.name
                    : this.referral.speciality?.name
            },

            getCreatedDate() {
                return dayjs(this.referral.createdAt).format('MMMM D, YYYY')
            },

            pageBreadcrumbs() {
                return [
                    {
                        label: this.$t('pages.referrals.pool.breadcrumbs.referrals'),
                        route: { name: `referrals.${this.$route.params.type}.pool` },
                    },
                    {
                        label: this.$t(`pages.referrals.index.breadcrumbs.${this.$route.params.type}`),
                        route: { name: `referrals.${this.$route.params.type}.pool` },
                    },
                    {
                        label: this.$t(`pages.referrals.show.breadcrumbs.${this.$route.params.type}`, {
                            name: this.getReferralTypeName,
                        }),
                    },
                ]
            },

            showOnlyProposedTimeSlots() {
                return (
                    this.referral?.id &&
                    this.type === ReferralPathTypeEnum.MADE &&
                    this.getUsersWithProposedTimeSlots?.length &&
                    this.referral?.status === ReferralStatusEnum.TIME_SLOTS_PROPOSED
                )
            },

            hideAsideWhenTimeSlotsProposed() {
                return this.referral?.status === ReferralStatusEnum.TIME_SLOTS_PROPOSED
            },

            showToHowManyProvidersIsSent() {
                return this.referral?.toProviders.length > 1 && this.referral?.status === ReferralStatusEnum.CREATED
            },
        },

        methods: {
            onCountryCodeChange(event) {
                this.emitEvent({ name: 'country-code-change', payload: event })
            },
            openUploadNoteModal() {
                this.isUploadNoteModalOpen = true
            },
            closeUploadNoteModal() {
                this.isUploadNoteModalOpen = false
            },
            setUploadedFiles(files) {
                this.uploadedFiles = files
                this.isUploadNoteModalOpen = false
            },
            removeFile(file) {
                this.uploadedFiles = this.uploadedFiles.filter((f) => f !== file)
                this.$refs.ehrFiles.acceptedFilesIds = this.$refs.ehrFiles.acceptedFilesIds.filter((id) => id !== file.file.id)
                this.$refs.uploadNoteModal.removeFile(file)
            },
            getReferralPoolById() {
                const { id: activeMemberId } = this.$auth.getActiveMember()
                ReferralsPoolAPI.getReferralPoolById(
                    this.$route.params.id,
                    this.type,
                    this.$auth.isPracticeAncillary() ? null : activeMemberId,
                    this.$auth.isPracticeAncillary() ? this.$auth.getPractice().id : null,
                )
                    .then((res) => {
                        this.referral = res
                        if (!this.data.selectedTimeslot) {
                            const { proposedDate, proposedTime } = this.$route.query
                            if (!proposedDate || !proposedTime) return

                            const formattedTime = proposedTime.replace('-', ':')
                            const value = `${proposedDate}|${formattedTime}`

                            if (this.proposedSlotOptions.some((opt) => opt.value === value)) {
                                this.$set(this.data, 'selectedTimeslot', value)
                            }
                        }
                    })
                    .catch(() => {
                        this.$router.go(-1)
                    })
            },

            groupSlotsByDateForUser(proposedTimeSlots) {
                const groupedSlots = proposedTimeSlots.reduce((acc, slot) => {
                    if (!acc[slot.date]) {
                        acc[slot.date] = []
                    }
                    acc[slot.date].push(slot)
                    return acc
                }, {})

                Object.keys(groupedSlots).forEach((date) => {
                    groupedSlots[date].sort((a, b) =>
                        dayjs(a.startTime, 'HH:mm').isBefore(dayjs(b.startTime, 'HH:mm')) ? -1 : 1,
                    )
                })

                return groupedSlots
            },

            openProvidersModal() {
                this.showReferralsPoolProvidersModal = true
            },

            closeProvidersModal() {
                this.showReferralsPoolProvidersModal = false
            },

            closeAvailableLocationsModal() {
                this.showAvailableLocationsModal = false
            },

            processReferral() {
                this.isProcessing = true
                PracticesAPI.acceptInsuranceAndTests(
                    this.$auth.getPractice().id,
                    [this.referral.insurancePlanId],
                    this.referral.referralTests,
                ).then(() => {
                    const query = {
                        address: this.referral.searchBy?.address || undefined,
                        city: this.referral.searchBy?.city || undefined,
                        state: this.referral.searchBy?.state || undefined,
                        zip: this.referral.searchBy?.zip,
                        patientId: this.referral.patientId,
                        referralPoolId: this.referral.id,
                    }
                    const request = this.$auth.isPracticeAncillary()
                        ? ReferralsPoolAPI.getAncillaryAvailableLocations(this.$auth.getPractice().id, query)
                        : ReferralsPoolAPI.getAvailableLocations(this.$auth.getActiveMember().id, query)

                    request
                        .then((response) => {
                            this.locations = response
                            if (this.referral?.toPracticeLocationId) {
                                this.goToTimeProposal({ id: this.referral.toPracticeLocationId })
                                return
                            }

                            if (this.locations.length > 1) {
                                this.showAvailableLocationsModal = true
                            } else {
                                if (this.$auth.getPractice().practiceType === PracticeTypeEnum.ANCILLARY) {
                                    this.goToTimeProposal({ id: 0 })
                                } else {
                                    this.goToTimeProposal(this.locations[0])
                                }
                            }
                        })
                        .finally(() => {
                            this.isProcessing = false
                        })
                })
            },

            formatDate(dateStr) {
                return dayjs(dateStr).format('DD MMM YYYY')
            },
            formatTime(timeStr) {
                return dayjs(timeStr, 'HH:mm').format('hh:mm A')
            },
            continueClick() {
                this.selectedUser = this.toProvider
                const key = this.data.selectedTimeslot
                if (!key) return
                this.selectedTimeslot = this.proposedSlotMap[key]
                this.confirmTimeSlot()
            },
            confirmTimeSlot() {
                if (this.isSsnEmpty && !this.areRequiredPatientFieldsFilled()) {
                    this.showRequiredFieldsError = true
                    return
                }
                const { date, startTime } = this.selectedTimeslot || {}

                if (!date || !startTime) {
                    return
                }

                const basePatient = this.referral.patient
                const formPatient = this.data?.patient || {}
                const patient = this.isSsnEmpty
                    ? {
                          ...basePatient,
                          firstName: formPatient.firstName,
                          lastName: formPatient.lastName,
                          ssn: formPatient.ssn,
                          dateOfBirth: formPatient.dateOfBirth,
                          mobilePhone: formPatient.mobilePhone,
                          mobilePhoneCountryCode: formPatient.mobilePhoneCountryCode,
                      }
                    : basePatient
                const uploadedDocsIds = this.filesAlreadyUploaded.map((file) => file.file?.id).filter(Boolean)

                const request = {
                    fromUserId: this.referral.fromUser?.id ?? this.referral.fromPractice?.masterUserId,
                    insurancePlanId: this.referral.insurancePlanId,
                    subSpecialityId: this.referral.subSpecialityId,
                    date,
                    startTime,
                    toPracticeLocationId: this.selectedUser?.practiceLocationId || 0,
                    isInsuranceAuthorizationRequired: this.referral.isInsuranceAuthorizationRequired,
                    isPrimaryCareOutsideMedmatch: this.referral.isPrimaryCareOutsideMedmatch,
                    patient,
                    diagnosis: this.referral.diagnosis,
                    icdCodeId: this.referral.icdCodeId,
                    insuranceGroupNumber: this.referral.insuranceGroupNumber,
                    insuranceNumber: this.referral.insuranceNumber,
                    note: this.referral.note,
                    isPatientAgreedToSms: this.referral.isPatientAgreedToSms,
                    referralNoteIds: [...(this.referral?.referralNoteIds || []), ...uploadedDocsIds],
                    primaryCarePhoneCountryCode: this.referral.primaryCarePhoneCountryCode,
                    primaryCareName: this.referral.primaryCareName,
                    primaryCareEmail: this.referral.primaryCareEmail,
                    primaryCarePhone: this.referral.primaryCarePhone,
                    primaryCareFax: this.referral.primaryCareFax,
                    referralPoolId: this.referral.id,
                }

                if (this.selectedUser.practiceType === PracticeTypeEnum.ANCILLARY) {
                    request.toPracticeId = this.selectedUser.id
                    request.toUserId = null
                    request.referralTests = this.referral.referralTests
                } else {
                    request.toUserId = this.selectedUser.id
                    request.toPracticeId = this.selectedUser.practiceId
                }
                ReferralsAPI.create(request).then(() => {
                    this.isAreYouSureModalOpen = false
                    this.isReferralMade = true
                })
            },

            printSummary() {
                window.print()
            },

            openAreYouSureModal(data, user) {
                this.showRequiredFieldsError = false
                this.selectedTimeslot = data
                this.selectedUser = user
                this.isAreYouSureModalOpen = true
            },

            closeAreYouSureModal() {
                this.selectedTimeslot = null
                this.selectedUser = null
                this.isAreYouSureModalOpen = false
            },

            expandItem(item) {
                const user = this.getUsersWithProposedTimeSlots.find((user) => user.id === item.id)
                if (user) {
                    user.isOpen = !user.isOpen
                }
            },

            goToTimeProposal(event) {
                this.$router.push({
                    name: `referrals.pool.propose.time`,
                    params: {
                        poolId: this.$route.params.id,
                        locationId: event.id,
                    },
                    query: {
                        physicianId: this.$auth.getActiveMember()?.id
                            ? this.$auth.getActiveMember()?.id
                            : this.$auth.getUser().id,
                        practiceId: this.$auth.getPractice().id,
                        userId: this.getUserId(),
                        practiceLocationId: event.id,
                        fromUserId: this.referral?.fromUser?.id,
                        timeFrame: this.referral?.timeframe,
                        fromDate:
                            this.referral?.timeframe === 'custom' && this.referral?.customDate
                                ? dayjs(this.referral.customDate).format('YYYY-MM-DD')
                                : dayjs(this.referral?.createdAt).format('YYYY-MM-DD'),
                        distance: event.distance ?? undefined,
                        practiceType: this.$auth.getPractice().practiceType,
                        isFromPatient: this.referral?.isFromPatient,
                        patientId: this.referral?.patientId,
                    },
                })
            },

            getUserId() {
                if (this.$auth.getPractice().practiceType !== PracticeTypeEnum.ANCILLARY) {
                    return this.$auth.getActiveMember()?.id ? this.$auth.getActiveMember()?.id : this.$auth.getUser().id
                }
            },

            areRequiredPatientFieldsFilled() {
                const patient = this.data?.patient || {}
                const requiredFields = [
                    patient?.firstName,
                    patient?.lastName,
                    patient?.dateOfBirth,
                    patient?.mobilePhone,
                    patient?.ssn,
                ]
                return requiredFields.every((field) => !!field && field.toString().trim().length > 0)
            },
        },
        watch: {
            $route() {
                this.getReferralPoolById()
            },
            'referral.patient': {
                immediate: true,
                deep: true,
                handler(patient) {
                    if (!patient) return
                    const prefills = {
                        patient: {
                            firstName: patient.firstName || '',
                            lastName: patient.lastName || '',
                            ssn: this.isSsnEmpty ? '' : patient.ssn || '',
                            dateOfBirth: patient.dateOfBirth || '',
                            mobilePhone: patient.mobilePhone || '',
                            mobilePhoneCountryCode: patient.mobilePhoneCountryCode || '+1',
                        },
                    }

                    this.prefillData(prefills)
                },
            },
            'data.patient': {
                deep: true,
                handler() {
                    if (this.showRequiredFieldsError && this.areRequiredPatientFieldsFilled()) {
                        this.showRequiredFieldsError = false
                    }
                },
            },
        },
        mounted() {
            this.getReferralPoolById()
        },
    }
</script>

<style lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';

    .slide-up-enter-active,
    .slide-up-leave-active {
        transition: max-height 0.5s ease-out, opacity 0.5s ease-out;
        overflow: hidden;
    }
    .slide-up-enter,
    .slide-up-leave-to {
        max-height: 0;
        opacity: 0;
        overflow: hidden;
    }
    .slide-up-enter-to,
    .slide-up-leave {
        max-height: 500px;
        opacity: 1;
        overflow: hidden;
    }

    .referral-pool-confirmation {
        .summary__item {
            @include breakpoint(smartphone-large) {
                flex-basis: 100%;
            }
            padding: 10px;
        }
    }

    .page-split__referrals-pools {
        .page-split__aside {
            padding: 10px;
            width: 100%;
        }

        .proposed-time-slots {
            max-height: calc(100vh - 450px);
            text-align: center;
            overflow-y: auto;
            overflow-x: hidden;
            display: flex;
            justify-content: space-around;
            align-items: flex-start;
            flex-wrap: wrap;
            margin: 10px 0;
            padding-right: 10px;

            &--user {
                display: flex;
                justify-content: space-between;
                color: $color--accent-1;
                align-items: center;
                cursor: pointer;
                padding: 5px 10px;
                border-top-left-radius: 10px;
                border-top-right-radius: 10px;

                .user-details-card {
                    margin-right: 15px;
                }

                &:hover {
                    background-color: $color--border-dropdown;
                }

                &--open {
                    background-color: $color--border-dropdown;
                    margin-bottom: 10px;
                }
            }

            &--item {
                border: 2px solid $color--accent-1;
                margin-bottom: 10px;
                border-radius: 10px;

                max-width: calc((100% - 60px) / 3);
                min-width: 340px;
                margin: 10px;

                &__times {
                    display: block;
                    flex-wrap: wrap;
                    justify-content: center;
                }
            }

            .slot-selection {
                display: flex;
                justify-content: center;
                margin-bottom: 10px;
                width: 100%;
                text-align: center;

                &:hover {
                    color: $color--accent-1;
                }
            }
        }

        .user-details-card {
            @include breakpoint(laptop) {
                display: flex;
                justify-content: center;
            }
        }
    }
    .required-fields-error {
        margin-top: 12px;
        color: #ff1111;
        font-size: 14px;
        font-weight: 500;
    }
    .patient-info-form {
        margin: 0px 200px;
        @include breakpoint(tablet) {
            margin: 0px;
        }
    }
    .continue_btn {
        margin-top: 30px;
    }
</style>
